import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { DashboardHeader } from "@/components/DashboardHeader";
import { ClinicalPageShell } from "@/components/clinical";
import { Button } from "@/components/ui/button";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import {
  Loader2,
  RefreshCw,
  Plus,
  X,
  Ticket,
  QrCode,
  ChevronDown,
  ChevronUp,
  CalendarDays,
  MapPin,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { useGooglePlacesAutocomplete } from "@/hooks/use-google-places";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";

export const Route = createFileRoute("/app/eventos")({
  component: EventosAdminPage,
  head: () => ({ meta: [{ title: "Eventos — Structura Market" }] }),
});

type EventRow = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  coverImageUrl: string | null;
  startsAt: string;
  endsAt: string | null;
  locationType: "presencial" | "online" | "hibrido";
  locationAddress: string | null;
  locationNotes: string | null;
  onlineLink: string | null;
  capacity: number;
  soldCount: number;
  price: number;
  currency: string;
  refundEnabled: boolean;
  refundHours: number | null;
  refundPolicy: string | null;
  status: "draft" | "active" | "sold_out" | "closed" | "cancelled";
};

type TicketRow = {
  id: string;
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string | null;
  quantity: number;
  qrCode: string;
  status: "pending" | "confirmed" | "redeemed" | "cancelled" | "refunded";
  createdAt: string;
};

type FormState = {
  title: string;
  description: string;
  startsAt: string;
  endsAt: string;
  locationType: "presencial" | "online" | "hibrido";
  locationAddress: string;
  locationNotes: string;
  onlineLink: string;
  capacity: string;
  price: string;
  refundEnabled: boolean;
  refundHours: string;
  refundPolicy: string;
  status: "draft" | "active" | "closed" | "cancelled";
};

const BLANK_FORM: FormState = {
  title: "",
  description: "",
  startsAt: "",
  endsAt: "",
  locationType: "presencial",
  locationAddress: "",
  locationNotes: "",
  onlineLink: "",
  capacity: "100",
  price: "0",
  refundEnabled: false,
  refundHours: "48",
  refundPolicy: "",
  status: "draft",
};

function statusLabel(s: EventRow["status"]) {
  const map: Record<EventRow["status"], string> = {
    draft: "Borrador",
    active: "Activo",
    sold_out: "Agotado",
    closed: "Cerrado",
    cancelled: "Cancelado",
  };
  return map[s] ?? s;
}

function statusBadgeClass(s: EventRow["status"]) {
  if (s === "active") return "bg-emerald-500/15 text-emerald-600";
  if (s === "sold_out") return "bg-orange-500/15 text-orange-600";
  if (s === "closed") return "bg-muted text-muted-foreground";
  if (s === "cancelled") return "bg-red-500/15 text-red-600";
  return "bg-muted text-muted-foreground";
}

function ticketStatusBadge(s: TicketRow["status"]) {
  const map: Record<TicketRow["status"], { label: string; cls: string }> = {
    pending: { label: "Pendiente", cls: "bg-amber-500/15 text-amber-700" },
    confirmed: { label: "Confirmado", cls: "bg-emerald-500/15 text-emerald-600" },
    redeemed: { label: "Utilizado", cls: "bg-blue-500/15 text-blue-600" },
    cancelled: { label: "Cancelado", cls: "bg-red-500/15 text-red-600" },
    refunded: { label: "Reembolsado", cls: "bg-purple-500/15 text-purple-600" },
  };
  return map[s] ?? { label: s, cls: "bg-muted text-muted-foreground" };
}

function eventToForm(e: EventRow): FormState {
  return {
    title: e.title,
    description: e.description ?? "",
    startsAt: e.startsAt ? e.startsAt.slice(0, 16) : "",
    endsAt: e.endsAt ? e.endsAt.slice(0, 16) : "",
    locationType: e.locationType,
    locationAddress: e.locationAddress ?? "",
    locationNotes: e.locationNotes ?? "",
    onlineLink: e.onlineLink ?? "",
    capacity: String(e.capacity),
    price: String(e.price),
    refundEnabled: e.refundEnabled,
    refundHours: e.refundHours != null ? String(e.refundHours) : "48",
    refundPolicy: e.refundPolicy ?? "",
    status: e.status === "sold_out" ? "active" : (e.status as FormState["status"]),
  };
}

function EventosAdminPage() {
  const tenantId = useWorkspaceTenantId();
  const [events, setEvents] = useState<EventRow[]>([]);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(BLANK_FORM);
  const [expandedTicketsId, setExpandedTicketsId] = useState<string | null>(null);
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setLock(null);
    try {
      const d = await apiGetJson<{ events: EventRow[] }>("/api/eventos", tenantId);
      setEvents(d.events ?? []);
    } catch (e: unknown) {
      const err = e as Error & { planLock?: PlanApiErrorPayload };
      if (err.planLock) setLock(err.planLock);
      else toast.error("No se pudieron cargar los eventos");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadTickets = async (eventId: string) => {
    if (expandedTicketsId === eventId) {
      setExpandedTicketsId(null);
      setTickets([]);
      return;
    }
    setExpandedTicketsId(eventId);
    setTicketsLoading(true);
    try {
      const d = await apiGetJson<{ tickets: TicketRow[] }>(`/api/eventos/tickets?eventId=${encodeURIComponent(eventId)}`);
      setTickets(d.tickets ?? []);
    } catch {
      toast.error("No se pudieron cargar los tickets");
      setTickets([]);
    } finally {
      setTicketsLoading(false);
    }
  };

  const openNew = () => {
    setSelectedId(null);
    setForm(BLANK_FORM);
    setShowForm(true);
  };

  const openEdit = (e: EventRow) => {
    setSelectedId(e.id);
    setForm(eventToForm(e));
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setSelectedId(null);
    setForm(BLANK_FORM);
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!form.title.trim() || !form.startsAt) {
      toast.error("Título y fecha de inicio son requeridos");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        startsAt: new Date(form.startsAt).toISOString(),
        endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
        locationType: form.locationType,
        locationAddress: form.locationAddress.trim() || null,
        locationNotes: form.locationNotes.trim() || null,
        onlineLink: form.onlineLink.trim() || null,
        capacity: Number(form.capacity) || 100,
        price: Number(form.price) || 0,
        refundEnabled: form.refundEnabled,
        refundHours: form.refundEnabled ? Number(form.refundHours) || 48 : null,
        refundPolicy: form.refundPolicy.trim() || null,
        status: form.status,
      };
      if (selectedId) {
        await apiSendJson("/api/eventos", "PUT", { id: selectedId, ...payload });
        toast.success("Evento actualizado");
      } else {
        await apiSendJson("/api/eventos", "POST", payload);
        toast.success("Evento creado");
      }
      closeForm();
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const setF = (k: keyof FormState, v: string | boolean) =>
    setForm((f) => ({ ...f, [k]: v }));

  const { setInputRef: setAddressInputRef, mapsEnabled } = useGooglePlacesAutocomplete((address) => {
    setF("locationAddress", address.formattedAddress || address.street);
  });

  const showAddress = form.locationType === "presencial" || form.locationType === "hibrido";
  const showOnlineLink = form.locationType === "online" || form.locationType === "hibrido";

  if (lock) return <FeatureLockedState payload={lock} />;

  return (
    <>
      <DashboardHeader title="Eventos" subtitle="Gestioná eventos, cupos y tickets de acceso" />
      <ClinicalPageShell offset="fixed-title">
        <MerchantExperienceBar screenId="eventos" />

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={openNew}>
          <Plus className="mr-1.5 h-4 w-4" /> Nuevo evento
        </Button>
        <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </Button>
        <Button size="sm" variant="outline" asChild>
          <Link to="/app/eventos/validar">
            <QrCode className="mr-1.5 h-4 w-4" /> Validador QR
          </Link>
        </Button>
      </div>

      {/* Formulario inline */}
      {showForm && (
        <div className="mb-6 rounded-2xl border border-border bg-card p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold text-foreground">
              {selectedId ? "Editar evento" : "Nuevo evento"}
            </h2>
            <Button size="icon" variant="ghost" onClick={closeForm}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <form onSubmit={(e) => void handleSubmit(e)} className="grid gap-4 sm:grid-cols-2">
            {/* Título */}
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium">Título *</label>
              <input
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.title}
                onChange={(e) => setF("title", e.target.value)}
                placeholder="Nombre del evento"
                required
              />
            </div>

            {/* Descripción */}
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium">Descripción</label>
              <textarea
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                rows={3}
                value={form.description}
                onChange={(e) => setF("description", e.target.value)}
                placeholder="Descripción del evento..."
              />
            </div>

            {/* Fecha inicio */}
            <div>
              <label className="mb-1 block text-sm font-medium">Inicio *</label>
              <input
                type="datetime-local"
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.startsAt}
                onChange={(e) => setF("startsAt", e.target.value)}
                required
              />
            </div>

            {/* Fecha fin */}
            <div>
              <label className="mb-1 block text-sm font-medium">Fin (opcional)</label>
              <input
                type="datetime-local"
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.endsAt}
                onChange={(e) => setF("endsAt", e.target.value)}
              />
            </div>

            {/* Tipo de ubicación */}
            <div>
              <label className="mb-1 block text-sm font-medium">Tipo de ubicación</label>
              <select
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.locationType}
                onChange={(e) => setF("locationType", e.target.value)}
              >
                <option value="presencial">Presencial</option>
                <option value="online">Online</option>
                <option value="hibrido">Híbrido</option>
              </select>
            </div>

            {/* Notas de ubicación */}
            <div>
              <label className="mb-1 block text-sm font-medium">Notas de ubicación</label>
              <input
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.locationNotes}
                onChange={(e) => setF("locationNotes", e.target.value)}
                placeholder="Piso 3, sala B, etc."
              />
            </div>

            {/* Dirección */}
            {showAddress && (
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium">Dirección</label>
                <input
                  ref={mapsEnabled ? setAddressInputRef : undefined}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={form.locationAddress}
                  onChange={(e) => setF("locationAddress", e.target.value)}
                  placeholder="Av. Corrientes 1234, Buenos Aires"
                />
              </div>
            )}

            {/* Link online */}
            {showOnlineLink && (
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium">Link online</label>
                <input
                  type="url"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={form.onlineLink}
                  onChange={(e) => setF("onlineLink", e.target.value)}
                  placeholder="https://meet.google.com/..."
                />
              </div>
            )}

            {/* Capacidad y precio */}
            <div>
              <label className="mb-1 block text-sm font-medium">Capacidad (cupos)</label>
              <input
                type="number"
                min={1}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.capacity}
                onChange={(e) => setF("capacity", e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Precio (ARS)</label>
              <input
                type="number"
                min={0}
                step="0.01"
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.price}
                onChange={(e) => setF("price", e.target.value)}
              />
            </div>

            {/* Reembolso */}
            <div className="flex items-center gap-3 sm:col-span-2">
              <button
                type="button"
                role="switch"
                aria-checked={form.refundEnabled}
                onClick={() => setF("refundEnabled", !form.refundEnabled)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${form.refundEnabled ? "bg-primary" : "bg-input"}`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform ${form.refundEnabled ? "translate-x-5" : "translate-x-0"}`}
                />
              </button>
              <span className="text-sm font-medium">Reembolso habilitado</span>
            </div>

            {form.refundEnabled && (
              <>
                <div>
                  <label className="mb-1 block text-sm font-medium">Horas de cancelación para reembolso</label>
                  <input
                    type="number"
                    min={1}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    value={form.refundHours}
                    onChange={(e) => setF("refundHours", e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Política de reembolso</label>
                  <textarea
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    rows={2}
                    value={form.refundPolicy}
                    onChange={(e) => setF("refundPolicy", e.target.value)}
                    placeholder="Describí la política de reembolso..."
                  />
                </div>
              </>
            )}

            {/* Estado */}
            <div>
              <label className="mb-1 block text-sm font-medium">Estado</label>
              <select
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={form.status}
                onChange={(e) => setF("status", e.target.value)}
              >
                <option value="draft">Borrador</option>
                <option value="active">Activo</option>
                <option value="closed">Cerrado</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </div>

            {/* Botones */}
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                {selectedId ? "Guardar cambios" : "Crear evento"}
              </Button>
              <Button type="button" variant="ghost" onClick={closeForm}>
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de eventos */}
      {loading ? (
        <div className="flex items-center gap-2 py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> Cargando eventos…
        </div>
      ) : events.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground">
          Sin eventos todavía. Creá tu primer evento con el botón "Nuevo evento".
        </p>
      ) : (
        <div className="space-y-3">
          {events.map((ev) => (
            <div key={ev.id} className="rounded-xl border border-border/60 bg-card/50 p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-foreground">{ev.title}</p>
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(ev.status)}`}
                    >
                      {statusLabel(ev.status)}
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <CalendarDays className="h-3.5 w-3.5" />
                      {format(parseISO(ev.startsAt), "d MMM yyyy HH:mm", { locale: es })}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" />
                      {ev.locationType === "presencial" ? (ev.locationAddress ?? "Presencial") : ev.locationType === "online" ? "Online" : "Híbrido"}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-3.5 w-3.5" />
                      {ev.soldCount}/{ev.capacity} cupos vendidos
                    </span>
                    <span className="font-medium text-foreground">
                      {ev.price > 0
                        ? new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(ev.price)
                        : "Gratuito"}
                    </span>
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(ev)}>
                    Editar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void loadTickets(ev.id)}>
                    <Ticket className="mr-1.5 h-4 w-4" />
                    Tickets
                    {expandedTicketsId === ev.id
                      ? <ChevronUp className="ml-1 h-3 w-3" />
                      : <ChevronDown className="ml-1 h-3 w-3" />}
                  </Button>
                </div>
              </div>

              {/* Panel de tickets */}
              {expandedTicketsId === ev.id && (
                <div className="mt-4 border-t border-border/50 pt-3">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Tickets del evento
                    </p>
                    <Button size="sm" variant="outline" asChild>
                      <Link to="/app/eventos/validar">
                        <QrCode className="mr-1.5 h-3.5 w-3.5" /> Abrir validador QR
                      </Link>
                    </Button>
                  </div>
                  {ticketsLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : tickets.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Sin tickets vendidos todavía</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-border/40 text-left text-muted-foreground">
                            <th className="pb-1.5 pr-4 font-medium">Comprador</th>
                            <th className="pb-1.5 pr-4 font-medium">Email</th>
                            <th className="pb-1.5 pr-4 font-medium">QR</th>
                            <th className="pb-1.5 pr-4 font-medium">Estado</th>
                            <th className="pb-1.5 font-medium">Fecha</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tickets.map((tk) => {
                            const badge = ticketStatusBadge(tk.status);
                            return (
                              <tr key={tk.id} className="border-b border-border/20 last:border-0">
                                <td className="py-2 pr-4 font-medium text-foreground">{tk.buyerName}</td>
                                <td className="py-2 pr-4 text-muted-foreground">{tk.buyerEmail}</td>
                                <td className="py-2 pr-4 font-mono text-[10px] text-muted-foreground">{tk.qrCode}</td>
                                <td className="py-2 pr-4">
                                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${badge.cls}`}>
                                    {badge.label}
                                  </span>
                                </td>
                                <td className="py-2 text-muted-foreground">
                                  {format(parseISO(tk.createdAt), "d MMM HH:mm", { locale: es })}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </ClinicalPageShell>
    </>
  );
}
